import { ref } from 'vue'
import {
  fetchWorkflowExport,
  WORKFLOW_BUNDLE_KIND,
  type WorkflowExportBundle,
} from '@/queries/workflows'

/**
 * Workflow execution state
 */
export interface WorkflowExecution {
  id: string
  status: 'running' | 'completed' | 'failed' | 'stopped'
  startTime: Date
  endTime?: Date
  progress: number
  currentNode?: string
  logs: ExecutionLog[]
}

/**
 * Execution log entry
 */
export interface ExecutionLog {
  timestamp: Date
  nodeId: string
  message: string
  level: 'info' | 'warning' | 'error'
  data?: any
}

/**
 * Composable for managing workflow operations
 * Handles execution, import/export, and workflow lifecycle operations
 */
export function useWorkflowOperations() {
  // Execution state
  const isRunning = ref(false)
  const currentExecution = ref<WorkflowExecution | null>(null)
  const executionHistory = ref<WorkflowExecution[]>([])

  /**
   * Execute workflow from start
   */
  function runWorkflow(nodes: any[], edges: any[]) {
    if (isRunning.value) {
      console.warn('Workflow is already running')
      return
    }

    const execution: WorkflowExecution = {
      id: `exec_${Date.now()}`,
      status: 'running',
      startTime: new Date(),
      progress: 0,
      logs: []
    }

    isRunning.value = true
    currentExecution.value = execution
    
    // Add to history
    executionHistory.value.unshift(execution)

    // Simulate workflow execution
    simulateWorkflowExecution(nodes, edges, execution)
  }

  /**
   * Stop current workflow execution
   */
  function stopWorkflow() {
    if (currentExecution.value && isRunning.value) {
      currentExecution.value.status = 'stopped'
      currentExecution.value.endTime = new Date()
      isRunning.value = false
      
      // Add stop log
      currentExecution.value.logs.push({
        timestamp: new Date(),
        nodeId: 'system',
        message: 'Workflow execution stopped by user',
        level: 'info'
      })
    }
  }

  /**
   * Result of picking + parsing an import file: either a valid bundle, or a reason it was rejected
   * (so the caller can surface a message rather than failing silently). `cancelled` = no file chosen.
   */
  type ImportFileResult =
    | { ok: true; bundle: WorkflowExportBundle }
    | { ok: false; cancelled: true }
    | { ok: false; cancelled: false; reason: string }

  /**
   * Open a file picker and read a workflow bundle. Only the envelope is validated here (it's a workflow
   * bundle of a known kind, with a services array); the server does the real content validation on
   * import. Returns the parsed bundle for the caller to POST — the import mutation lives where the
   * workflow context (and query invalidation) does.
   */
  function readImportFile(): Promise<ImportFileResult> {
    return new Promise((resolve) => {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = '.json,application/json'

      input.onchange = (event) => {
        const file = (event.target as HTMLInputElement).files?.[0]
        if (!file) {
          resolve({ ok: false, cancelled: true })
          return
        }

        const reader = new FileReader()
        reader.onload = (e) => {
          let data: unknown
          try {
            data = JSON.parse(e.target?.result as string)
          } catch {
            resolve({ ok: false, cancelled: false, reason: 'That file is not valid JSON.' })
            return
          }

          const bundle = data as Partial<WorkflowExportBundle>
          if (bundle?.kind !== WORKFLOW_BUNDLE_KIND || !Array.isArray(bundle?.services)) {
            resolve({ ok: false, cancelled: false, reason: 'That file is not a workflow export.' })
            return
          }

          resolve({ ok: true, bundle: bundle as WorkflowExportBundle })
        }

        reader.readAsText(file)
      }

      input.click()
    })
  }

  /**
   * Fetch the current workflow's export bundle from the server and download it as a JSON file.
   */
  async function exportWorkflow(workflowId: string, workflowName: string = 'workflow') {
    const bundle = await fetchWorkflowExport(workflowId)

    const json = JSON.stringify(bundle, null, 2)
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)

    const fileName = `${workflowName.replace(/\s+/g, '_').toLowerCase()}_${new Date().toISOString().split('T')[0]}.json`
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    link.click()

    URL.revokeObjectURL(url)
  }

  /**
   * Reset workflow to initial state
   */
  function resetWorkflow(nodes: any[]) {
    // Stop any running execution
    stopWorkflow()
    
    // Reset all node states
    nodes.forEach(node => {
      if (node.data) {
        node.data.processing = false
        node.data.running = false
        node.data.completed = false
        node.data.failed = false
        
        // Clear execution data
        delete node.data.executionData
        delete node.data.runData
      }
    })
  }

  /**
   * Simulate workflow execution (for demo purposes)
   */
  function simulateWorkflowExecution(nodes: any[], edges: any[], execution: WorkflowExecution) {
    const nodesCopy = [...nodes]
    let currentIndex = 0
    
    const executeNextNode = () => {
      if (currentIndex >= nodesCopy.length || execution.status !== 'running') {
        // Complete execution
        execution.status = 'completed'
        execution.endTime = new Date()
        execution.progress = 100
        isRunning.value = false
        
        execution.logs.push({
          timestamp: new Date(),
          nodeId: 'system',
          message: 'Workflow execution completed successfully',
          level: 'info'
        })
        return
      }

      const node = nodesCopy[currentIndex]
      execution.currentNode = node.id
      execution.progress = Math.round(((currentIndex + 1) / nodesCopy.length) * 100)

      // Update node state
      if (node.data) {
        node.data.processing = true
        node.data.running = true
      }

      // Add execution log
      execution.logs.push({
        timestamp: new Date(),
        nodeId: node.id,
        message: `Executing ${node.data?.label || node.id}`,
        level: 'info'
      })

      // Simulate processing time
      setTimeout(() => {
        if (execution.status === 'running' && node.data) {
          node.data.processing = false
          node.data.running = false
          node.data.completed = true
          
          execution.logs.push({
            timestamp: new Date(),
            nodeId: node.id,
            message: `Completed ${node.data.label || node.id}`,
            level: 'info'
          })
          
          currentIndex++
          executeNextNode()
        }
      }, Math.random() * 2000 + 500) // Random delay between 0.5-2.5 seconds
    }

    executeNextNode()
  }

  /**
   * Get execution logs for a specific node
   */
  function getNodeExecutionLogs(nodeId: string): ExecutionLog[] {
    if (!currentExecution.value) return []
    
    return currentExecution.value.logs.filter(log => log.nodeId === nodeId)
  }

  /**
   * Get current execution status
   */
  function getExecutionStatus() {
    return {
      isRunning: isRunning.value,
      execution: currentExecution.value,
      progress: currentExecution.value?.progress || 0,
      currentNode: currentExecution.value?.currentNode,
      logs: currentExecution.value?.logs || []
    }
  }

  return {
    // State
    isRunning,
    currentExecution,
    executionHistory,
    
    // Methods
    runWorkflow,
    stopWorkflow,
    resetWorkflow,
    readImportFile,
    exportWorkflow,
    getNodeExecutionLogs,
    getExecutionStatus,
  }
}